import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Package,
  Tag,
  IndianRupee,
  Image as ImageIcon,
  Loader2,
  Edit,
  Trash2,
  User,
  Calendar,
  MapPin,
  Mail,
  Phone,
  Building2,
  FileText,
  TrendingUp,
  Filter,
  Download,
} from "lucide-react";
import Breadcrumb from "../../../components/AdminSideComponents/BreadCrumb";
import { showError, showSuccess, confirmAction } from "../../../utils/toastUtils";
import AddProductDrawer from "./../../../components/AdminSideComponents/products/AddProductDrawer";
import { useAdminAuth } from "../../../context/AdminAuthContext";

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [assignmentHistory, setAssignmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // filters
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedBranch, setSelectedBranch] = useState("");

  // branches list (only for superadmin)
  const [branches, setBranches] = useState([]);

  const { isAdmin } = useAdminAuth();
  const ITEMS_PER_PAGE = 10;

  useEffect(() => {
    if (productId) {
      fetchProductDetails();
      fetchAssignmentHistory();
    }
  }, [productId, selectedStatus, selectedBranch]);

  useEffect(() => {
    if (isAdmin) {
      fetchBranches();
    }
  }, [isAdmin]);

  const fetchBranches = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/branches/get-all`
      );
      setBranches(res.data.data || []);
    } catch (error) {
      console.error("Failed to fetch branches:", error);
    }
  };

  const fetchProductDetails = async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/products/${productId}`
      );
      setProduct(response.data.data);
    } catch (error) {
      console.error("Failed to fetch product:", error);
      showError("Failed to load product details");
      navigate("/products-management/all-products");
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignmentHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/product-requests/get-all`,
        {
          params: {
            productId,
            status: selectedStatus || undefined,

            // send branch ONLY for superadmin
            ...(isAdmin && selectedBranch && { branch: selectedBranch }),
          },
        }
      );

      setAssignmentHistory(response.data.data || []);
    } catch (error) {
      console.error("Failed to fetch product requests:", error);
      setAssignmentHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "verified":
        return "bg-purple-100 text-purple-800";
      case "assigned":
        return "bg-blue-100 text-blue-800";
      case "recieved":
        return "bg-indigo-100 text-indigo-800";
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const exportToPDF = async () => {
    try {
      showSuccess("Preparing PDF export...");

      const { jsPDF } = await import("jspdf");
      const autoTable = (await import("jspdf-autotable")).default;

      const doc = new jsPDF({
        orientation: "landscape",
        unit: "mm",
        format: "a4",
      });

      const marginX = 10;

      // Title
      doc.setFontSize(16);
      doc.text(
        `PRODUCT REQUEST HISTORY - ${product.productName.toUpperCase()}`,
        marginX,
        14
      );

      // Prepare table data
      const tableBody = assignmentHistory.flatMap((request) => {
        const productEntry = request.products.find(
          (p) => p.product._id === product._id
        );

        if (!productEntry) return [];

        return [[
          new Date(request.createdAt).toLocaleDateString("en-IN"),
          request._id.slice(-8).toUpperCase(),
          (request.branch?.branchName || "—").toUpperCase(),
          (request.staff?.name || "UNKNOWN").toUpperCase(),
          productEntry.requestedQuantity,
          productEntry.assignedQuantity,
          productEntry.status.toUpperCase(),
          productEntry.product.productUsageType === "reusable"
            ? `${productEntry.returnStatus.toUpperCase()} (${productEntry.returnedQuantity})`
            : "NOT REQUIRED",
          request.status.toUpperCase(),
        ]];
      });

      autoTable(doc, {
        startY: 22,
        margin: { left: marginX, right: marginX },
        head: [[
          "DATE",
          "REQUEST ID",
          "BRANCH",
          "STAFF",
          "REQUESTED",
          "ASSIGNED",
          "PRODUCT STATUS",
          "RETURN INFO",
          "REQUEST STATUS",
        ]],
        body: tableBody,
        styles: {
          fontSize: 8,
          cellPadding: 2,
        },
        headStyles: {
          fillColor: [99, 102, 241], // indigo
          textColor: 255,
          fontSize: 9,
        },
      });

      doc.save(
        `${product.productName.replace(/\s+/g, "_")}_Requests.pdf`
      );

      showSuccess("PDF exported successfully!");
    } catch (error) {
      console.error("PDF export failed:", error);
      showError("Failed to export PDF. Please try again.");
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader2 className="animate-spin text-blue-500 mx-auto mb-4" size={48} />
          <p className="text-gray-600 font-medium">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen gap-4 bg-gray-50">
        <div className="bg-white p-8 rounded-2xl shadow-lg text-center">
          <Package size={64} className="text-gray-300 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-800 mb-2">Product not found</h3>
          <p className="text-gray-600 mb-4">The product you're looking for doesn't exist.</p>
          <button
            onClick={() => navigate("/products-management/all-products")}
            className="px-6 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition flex items-center gap-2 mx-auto"
          >
            <ArrowLeft size={18} />
            Back to Products
          </button>
        </div>
      </div>
    );
  }

  // Calculate prices
  const basePrice = product.mrp * product.quantity;
  const discountAmount = (basePrice * product.discountPercent) / 100;
  const finalPrice = basePrice - discountAmount;
  const unitPrice = product.mrp - (product.mrp * product.discountPercent) / 100;
  // console.log(product.mrp-(product.discountPercent * product.mrp)/100)

  // Pagination
  const totalPages = Math.ceil(assignmentHistory.length / ITEMS_PER_PAGE);
  const paginatedHistory = assignmentHistory.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  // Calculate statistics
  const totalRequested = assignmentHistory.reduce((sum, req) => {
    const productEntry = req.products.find(p => p.product._id === product._id);
    return sum + (productEntry?.requestedQuantity || 0);
  }, 0);

  const totalAssigned = assignmentHistory.reduce((sum, req) => {
    const productEntry = req.products.find(p => p.product._id === product._id);
    return sum + (productEntry?.assignedQuantity || 0);
  }, 0);

  return (
    <div className="">
      {/* Breadcrumb */}
      <div className="px-4 py-3">
        <div className="flex items-center justify-between">
          <Breadcrumb
            title1="Products"
            title2="Products Management"
            title3={product.productName}
          />

          <button
            onClick={() => navigate("/products-management/all-products")}
            className="flex items-center gap-2 px-4 py-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Back to Products</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-3 sm:px-4 pb-6 max-w-7xl mx-auto">

        {/* Product Overview Card */}
        <div className="bg-white border border-[#F7BFAD] rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row gap-6">
              {/* Product Image */}
              {/* <div className="flex-shrink-0">
                {product.productImg ? (
                  <div className="relative group">
                    <img
                      src={product.productImg}
                      alt={product.productName}
                      className="w-full lg:w-48 h-48 object-cover rounded-xl border-2 border-gray-200 shadow-md group-hover:shadow-xl transition"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition rounded-xl" />
                  </div>
                ) : (
                  <div className="w-full lg:w-48 h-48 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border-2 border-dashed border-gray-300">
                    <ImageIcon size={48} className="text-gray-400" />
                  </div>
                )}
              </div> */}

              {/* Product Info */}
              <div className="flex-1 space-y-5">
                {/* Title & Badges */}
                <div className="flex flex-col gap-3">
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-700 capitalize">
                    {product.productName}
                  </h2>

                  <div className="flex flex-wrap gap-2 items-center">
                    <span className="flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-gradient-to-r from-indigo-100 to-indigo-200 text-indigo-700 font-semibold text-sm">
                      <Tag size={14} />
                      {product.brand}
                    </span>


                    <span
                      className={`px-4 py-1.5 rounded-full text-sm font-bold ${product.productUsageType === "use_and_throw"
                        ? "bg-gradient-to-r from-orange-100 to-orange-200 text-orange-700"
                        : "bg-gradient-to-r from-green-100 to-green-200 text-green-700"
                        }`}
                    >
                      {product.productUsageType === "use_and_throw"
                        ? "Use & Throw"
                        : "Reusable"}
                    </span>

                    <span className="px-4 py-1.5 rounded-full bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 font-bold text-sm">
                      Stock: {product.quantity}
                    </span>
                  </div>
                </div>

                {/* Vendor Details */}
                {product.vendor && (
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 border border-gray-200 rounded-xl p-5 space-y-3">
                    <div className="flex items-center gap-2 text-sm font-bold text-gray-800 border-b border-gray-300 pb-2">
                      <Building2 size={18} className="text-indigo-600" />
                      <span>Vendor Information</span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-500" />
                        <span className="text-gray-600">Vendor:</span>
                        <span className="font-semibold text-gray-900">
                          {product.vendor.vendorName}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <User size={16} className="text-gray-500" />
                        <span className="text-gray-600">Owner:</span>
                        <span className="font-semibold text-gray-900">
                          {product.vendor.ownerName}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Mail size={16} className="text-gray-500" />
                        <span className="text-gray-600">Email:</span>
                        <span className="font-medium text-gray-800 truncate">
                          {product.vendor.email}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Phone size={16} className="text-gray-500" />
                        <span className="text-gray-600">Phone:</span>
                        <span className="font-medium text-gray-800">
                          {product.vendor.phone}
                        </span>
                      </div>
                    </div>

                    {!product.vendor.isActive && (
                      <div className="pt-2">
                        <span className="inline-flex px-3 py-1.5 rounded-full text-xs font-bold bg-red-100 text-red-700 border border-red-200">
                          ⚠️ Inactive Vendor
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Price Section */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-4 text-center">
                    <p className="text-xs text-blue-700 uppercase tracking-wide font-semibold mb-1">
                      MRP
                    </p>
                    <p className="flex items-center justify-center text-xl font-bold text-blue-900">
                      <IndianRupee size={18} />
                      {product.mrp.toFixed(2)}
                    </p>
                  </div>

                  {product.discountPercent > 0 && (
                    <>
                      <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-green-700 uppercase tracking-wide font-semibold mb-1">
                          Discount
                        </p>
                        <p className="text-xl font-bold text-green-900">
                          {product.discountPercent}%
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-purple-700 uppercase tracking-wide font-semibold mb-1">
                          Final Price
                        </p>
                        <p className="flex items-center justify-center text-xl font-bold text-purple-900">
                          <IndianRupee size={18} />
                          {finalPrice.toFixed(2)}
                        </p>
                      </div>

                      <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-xl p-4 text-center">
                        <p className="text-xs text-orange-700 uppercase tracking-wide font-semibold mb-1">
                          Unit Price
                        </p>
                        <p className="flex items-center justify-center text-xl font-bold text-orange-900">
                          <IndianRupee size={18} />
                          {unitPrice}
                        </p>
                      </div>
                    </>
                  )}
                </div>

                {/* Description */}
                {product.productDescription && (
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-xs text-gray-500 uppercase tracking-wide font-semibold mb-2">
                      Description
                    </p>
                    <p className="text-sm text-gray-700 leading-relaxed">
                      {product.productDescription}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4 mb-6">

          {/* Total Requests */}
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-1.5 sm:p-3 shadow-sm hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-blue-700 via-blue-300 to-blue-700 rounded-lg flex items-center justify-center">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-blue-700 font-semibold mt-2">
                Total Requests
              </p>
            </div>

            <div className="text-right">
              <p className="text-2xl sm:text-3xl font-bold text-blue-900">
                {assignmentHistory.length}
              </p>
              <p className="text-xs text-blue-600 mt-2 font-semibold capitalize">
                All requests
              </p>
            </div>
          </div>

          {/* Total Requested */}
          <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 border border-yellow-200 rounded-xl p-1.5 sm:p-3 shadow-sm hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-yellow-700 via-yellow-300 to-yellow-700 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-yellow-700 font-semibold mt-2">
                Total Requested
              </p>
            </div>

            <div className="text-right">
              <p className="text-2xl sm:text-3xl font-bold text-yellow-900">
                {totalRequested}
              </p>
              <p className="text-xs text-yellow-600 mt-2 font-semibold capitalize">
                Quantity asked
              </p>
            </div>
          </div>

          {/* Total Assigned */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-xl p-1.5 sm:p-3 shadow-sm hover:shadow-md transition flex items-center justify-between">
            <div>
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-700 via-green-300 to-green-700 rounded-lg flex items-center justify-center">
                <Package className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
              </div>
              <p className="text-xs sm:text-sm text-green-700 font-semibold mt-2">
                Total Assigned
              </p>
            </div>

            <div className="text-right">
              <p className="text-2xl sm:text-3xl font-bold text-green-900">
                {totalAssigned}
              </p>
              <p className="text-xs text-green-600 mt-2 font-semibold capitalize">
                Successfully issued
              </p>
            </div>
          </div>

        </div>


        {/* Product Requests History */}
        <div className="bg-white rounded-2xl shadow-xl border border-[#F7BFAD]  overflow-hidden">
          <div className="px-6 py-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <h2 className="text-xl text-gray-700 font-bold flex items-center gap-2">
                <Calendar size={24} />
                Product Request History
              </h2>
              <div className="flex items-center gap-3">
                <span className="px-4 py-1.5 bg-black/20 backdrop-blur-sm text-gray-800 rounded-full text-sm font-bold border border-white/30">
                  {assignmentHistory.length} Requests
                </span>
                {assignmentHistory.length > 0 && (
                  <button
                    onClick={exportToPDF}
                    className="px-4 py-1.5 bg-primaryBtnColor text-white rounded-lg hover:bg-primaryBtnHoverColor transition flex items-center gap-2 text-sm font-semibold"
                  >
                    <Download size={16} />
                    <span className="hidden sm:inline">Export</span>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
            <div className="flex flex-wrap gap-3 items-center">
              <Filter size={18} className="text-gray-500" />

              {/* Status Filter */}
              <select
                value={selectedStatus}
                onChange={(e) => {
                  setSelectedStatus(e.target.value);
                  setCurrentPage(1);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primaryColor bg-white font-medium"
              >
                <option value="">All Status</option>
                <option value="pending">Pending</option>
                <option value="verified">Verified</option>
              </select>

              {/* Branch Filter (SUPER ADMIN ONLY) */}
              {isAdmin && (
                <select
                  value={selectedBranch}
                  onChange={(e) => {
                    setSelectedBranch(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-primaryColor bg-white font-medium"
                >
                  <option value="">All Branches</option>
                  {branches.map((branch) => (
                    <option key={branch._id} value={branch._id}>
                      {branch.branchName}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="p-6">
            {/* Loading */}
            {loadingHistory ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Loader2 className="animate-spin text-purple-500 mx-auto mb-3" size={40} />
                  <p className="text-sm text-gray-600">Loading requests...</p>
                </div>
              </div>
            ) : assignmentHistory.length === 0 ? (
              <div className="text-center py-16 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border-2 border-dashed border-gray-300">
                <Package size={56} className="mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold text-gray-700 mb-2">No Requests Found</h3>
                <p className="text-sm text-gray-500">
                  {selectedStatus || selectedBranch
                    ? "No requests match your current filters"
                    : "No requests found for this product"}
                </p>
              </div>
            ) : (
              <>
                {/* Table */}
                <div className="overflow-x-auto border border-gray-200 rounded-xl">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gradient-to-r from-gray-100 to-gray-200">
                      <tr className="text-gray-700">
                        <th className="px-4 py-3 text-left font-bold">Date</th>
                        <th className="px-4 py-3 text-left font-bold">Request ID</th>
                        <th className="px-4 py-3 text-left font-bold">Branch</th>
                        <th className="px-4 py-3 text-left font-bold">Staff</th>
                        <th className="px-4 py-3 text-center font-bold">Requested</th>
                        <th className="px-4 py-3 text-center font-bold">Assigned</th>
                        <th className="px-4 py-3 text-center font-bold">Product Status</th>
                        <th className="px-4 py-3 text-center font-bold">Return Info</th>
                        <th className="px-4 py-3 text-center font-bold">Request Status</th>
                      </tr>
                    </thead>

                    <tbody className="divide-y divide-gray-200">
                      {paginatedHistory.map((request) => {
                        const productEntry = request.products.find(
                          (p) => p.product._id === product._id
                        );

                        if (!productEntry) return null;

                        const isReusable = productEntry.product?.productUsageType === "reusable";
                        const hasReturned = productEntry.returnedQuantity > 0;
                        const hasDamaged = productEntry.damagedQuantity > 0;
                        const hasRemark = productEntry.returnRemark?.trim();

                        return (
                          <tr key={request._id} className="hover:bg-purple-50/50 transition">
                            {/* Date */}
                            <td className="px-4 py-3 whitespace-nowrap text-gray-700 font-medium">
                              {new Date(request.createdAt).toLocaleDateString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                              })}
                            </td>

                            {/* Request ID */}
                            <td className="px-4 py-3 font-mono text-xs text-gray-700 font-semibold">
                              #{request._id.slice(-8).toUpperCase()}
                            </td>

                            {/* Branch */}
                            <td className="px-4 py-3 text-gray-800 font-medium">
                              {request.branch?.branchName || "—"}
                            </td>

                            {/* Staff */}
                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-800">
                                {request.staff?.name || "Unknown"}
                              </div>
                              <div className="text-xs text-gray-500 truncate max-w-[180px]">
                                {request.staff?.email || "—"}
                              </div>
                            </td>

                            {/* Requested Qty */}
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full font-bold text-sm">
                                {productEntry.requestedQuantity}
                              </span>
                            </td>

                            {/* Assigned Qty */}
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex px-3 py-1 bg-blue-100 text-blue-800 rounded-full font-bold text-sm">
                                {productEntry.assignedQuantity || 0}
                              </span>
                            </td>

                            {/* Product Status */}
                            <td className="px-4 py-3 text-center">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold capitalize ${productEntry.status === "approved" ? "bg-green-100 text-green-800" :
                                productEntry.status === "rejected" ? "bg-red-100 text-red-800" :
                                  "bg-yellow-100 text-yellow-800"
                                }`}>
                                {productEntry.status}
                              </span>
                            </td>

                            {/* Return / Damage Info – Improved Column */}
                            <td className="px-3 py-3 text-center text-xs">
                              {isReusable ? (
                                <div className="flex flex-col items-center gap-1 min-w-[120px]">
                                  {productEntry.returnStatus === "not_required" ? (
                                    <span className="text-gray-400 font-medium">Not Required</span>
                                  ) : (
                                    <>
                                      {/* Status Badge */}
                                      <span className={`inline-flex px-2.5 py-0.5 rounded-full font-medium text-[11px] ${productEntry.returnStatus === "returned" ? "bg-green-100 text-green-700" :
                                        productEntry.returnStatus === "damaged" ? "bg-red-100 text-red-700" :
                                          productEntry.returnStatus === "partially_returned" ? "bg-purple-100 text-purple-700" :
                                            "bg-yellow-100 text-yellow-700"
                                        }`}>
                                        {productEntry.returnStatus === "partially_returned"
                                          ? "Part. Returned"
                                          : productEntry.returnStatus.charAt(0).toUpperCase() + productEntry.returnStatus.slice(1)}
                                      </span>

                                      {/* Quantities */}
                                      <div className="flex gap-3 text-[11px] font-medium mt-1">
                                        {hasReturned && (
                                          <div className="text-green-700">
                                            Ret: <strong>{productEntry.returnedQuantity}</strong>
                                          </div>
                                        )}
                                        {hasDamaged && (
                                          <div className="text-red-700">
                                            Dmg: <strong>{productEntry.damagedQuantity}</strong>
                                          </div>
                                        )}
                                        {!hasReturned && !hasDamaged && productEntry.returnStatus === "pending" && (
                                          <span className="text-gray-500">Pending</span>
                                        )}
                                      </div>

                                      {/* Return Remark (only if exists) */}
                                      {hasRemark && (
                                        <div className="mt-1 text-center w-full max-w-[180px] text-[10px] text-gray-600 italic bg-gray-50/80 px-2 py-1 rounded">
                                          {productEntry.returnRemark}
                                        </div>
                                      )}
                                    </>
                                  )}
                                </div>
                              ) : (
                                <span className="text-gray-400 font-medium text-xs">Not Required</span>
                              )}
                            </td>

                            {/* Overall Request Status */}
                            <td className="px-4 py-3 text-center">
                              <span
                                className={`inline-flex px-3 py-1 rounded-full text-xs font-bold capitalize ${getStatusBadge(
                                  request.status
                                )}`}
                              >
                                {request.status}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex flex-col sm:flex-row items-center justify-between mt-6 gap-4">
                    <p className="text-sm text-gray-600 font-medium">
                      Showing {(currentPage - 1) * ITEMS_PER_PAGE + 1} to{" "}
                      {Math.min(currentPage * ITEMS_PER_PAGE, assignmentHistory.length)} of{" "}
                      {assignmentHistory.length} requests
                    </p>

                    <div className="flex gap-2">
                      <button
                        disabled={currentPage === 1}
                        onClick={() => setCurrentPage((p) => p - 1)}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition font-medium text-sm"
                      >
                        Previous
                      </button>
                      <button
                        disabled={currentPage === totalPages}
                        onClick={() => setCurrentPage((p) => p + 1)}
                        className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition font-medium text-sm"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Edit Product Drawer */}
      <AddProductDrawer
        isOpen={isEditDrawerOpen}
        editingProduct={product}
        onClose={() => setIsEditDrawerOpen(false)}
        onAddProduct={() => {
          setIsEditDrawerOpen(false);
          fetchProductDetails();
          fetchAssignmentHistory();
        }}
      />
    </div>
  );
};

export default ProductDetails;